import React, { Component } from "react";
import {
    GET,
    loading,
    NavbarWarehouse,
    format_date,
    t
} from "../../components/CustomComponent.js";
import { Chart as ChartJS, ArcElement, Tooltip, Legend, CategoryScale, LinearScale, BarElement, Title } from 'chart.js';
import { Pie } from 'react-chartjs-2';

ChartJS.register(ArcElement, Tooltip, Legend, CategoryScale, LinearScale, BarElement, Title);

export default class WarehouseDashboard extends Component {
    constructor(props) {
        super(props);
        this.state = {
            summary: {
                total_item: 0,
                balance: 0,
                in_stock: 0,
                low_stock: 0,
                out_of_stock: 0,
            },
            low_stock_items: [],
            purchase_orders: [],
            movement_summary: [],
        };
    }

    async componentDidMount() {
        await this.Get();
        loading(false);
    }

    Get = async () => {
        loading(true);

        const today = new Date();
        const pad = (n) => String(n).padStart(2, "0");
        const dateStr = `${today.getFullYear()}-${pad(today.getMonth() + 1)}-${pad(today.getDate())}`;

        // นับระดับ Batch (view=lot) ให้ตรงกับหน้า Stock ไม่งั้น "รายการทั้งหมด" ที่นี่จะน้อยกว่าหน้า Stock
        // เพราะตาราง stocks ยุบหลาย Batch ของอะไหล่+คลัง+พื้นที่เก็บเดียวกันเป็นแถวเดียว
        const [resSummary, resLowStock, resPO, resMovement] = await Promise.all([
            GET("warehouse/stocks/summary", { view: "lot" }),
            GET("warehouse/stocks", { status: "LowStock", page: 1, page_size: 8, view: "lot" }),
            GET("warehouse/purchase-orders", { page: 1, page_size: 6 }),
            GET("warehouse/stock-movements/summary", {
                start_date: `${dateStr} 00:00:00`,
                end_date: `${dateStr} 23:59:59`,
            }),
        ]);

        this.setState({
            summary: resSummary?.status
                ? {
                    total_item: resSummary.data?.total_item || 0,
                    balance: resSummary.data?.balance || 0,
                    in_stock: resSummary.data?.in_stock || 0,
                    low_stock: resSummary.data?.low_stock || 0,
                    out_of_stock: resSummary.data?.out_of_stock || 0,
                }
                : this.state.summary,
            low_stock_items: resLowStock?.status ? resLowStock.data || [] : [],
            purchase_orders: resPO?.status ? resPO.data || [] : [],
            movement_summary: resMovement?.status ? resMovement.data || [] : [],
        });

        loading(false);
    };

    formatNumber = (num) => Number(num || 0).toLocaleString("en-US");

    movementMeta = (type) => {
        const map = {
            receipt: { label: t("movement_receipt"), color: "success", icon: "" },
            issue: { label: t("movement_issue"), color: "primary", icon: "" },
            transfer: { label: t("movement_transfer"), color: "purple", icon: "" },
            return: { label: t("movement_return"), color: "info", icon: "" },
            adjustment: { label: t("movement_adjustment"), color: "warning", icon: "" },
            stocktake: { label: t("movement_stocktake"), color: "secondary", icon: "" },
        };
        return map[type] || { label: type || "-", color: "secondary", icon: "" };
    };

    poStatusMeta = (status) => {
        const map = {
            pending: { label: t("po_status_pending"), color: "warning" },
            pending_approval: { label: t("po_status_pending_approval"), color: "warning" },
            approved: { label: t("po_status_approved"), color: "success" },
            sent: { label: t("po_status_sent"), color: "info" },
            partial_received: { label: t("po_status_partial"), color: "info" },
            received: { label: t("po_status_received"), color: "success" },
            cancelled: { label: t("po_status_cancelled"), color: "danger" },
        };
        return map[status] || { label: (status || "-").replaceAll("_", " "), color: "secondary" };
    };

    // ใช้สีสถานะให้ตรงกับหน้ารายการ PO (pr-po.js → getStatusBadgeClass)
    poStatusBadgeClass = (status) => {
        const statusKey = (status || "").toLowerCase();
        if (statusKey === "pending" || statusKey === "pending_approval") return "bg-warning-light text-warning border border-warning";
        if (statusKey === "approved" || statusKey === "received") return "bg-success-light text-success border border-success";
        if (statusKey === "partial" || statusKey === "partial_received") return "bg-info-light text-info border border-info";
        if (statusKey === "cancel" || statusKey === "cancelled" || statusKey === "rejected") return "bg-danger-light text-danger border border-danger";
        if (statusKey === "sent" || statusKey === "sent_to_supplier") return "bg-info-light text-info border border-info";
        if (statusKey === "awaiting_receipt") return "bg-purple-light text-purple border border-purple";
        return "bg-light text-dark border";
    };

    render() {
        const { summary, low_stock_items, purchase_orders, movement_summary } = this.state;

        const colors = {
            primary: { bg: '#e0e7ff', text: '#4f46e5' },
            success: { bg: '#dcfce7', text: '#16a34a' },
            warning: { bg: '#fef3c7', text: '#d97706' },
            danger: { bg: '#fee2e2', text: '#dc2626' },
            purple: { bg: '#f3e8ff', text: '#9333ea' },
            info: { bg: '#e0f2fe', text: '#0284c7' },
            secondary: { bg: '#f1f5f9', text: '#64748b' },
        };

        // backend นับ in_stock มาให้แล้ว (in + low + out = total) ใช้ค่านั้นตรง ๆ จะได้ไม่เพี้ยนจากการลบเอง
        const inStock = summary.in_stock || Math.max(summary.total_item - summary.low_stock - summary.out_of_stock, 0);
        const hasStock = summary.total_item > 0;

        const stockStatusData = {
            labels: [t("in_stock"), t("low_stock"), t("out_of_stock")],
            datasets: [{
                data: [inStock, summary.low_stock, summary.out_of_stock],
                backgroundColor: ['#16a34a', '#f59e0b', '#dc2626'],
                borderWidth: 0,
            }],
        };
        const stockStatusOptions = {
            responsive: true,
            maintainAspectRatio: false,
            plugins: {
                legend: { position: 'bottom', labels: { boxWidth: 12, padding: 12, font: { size: 12 } } },
            },
        };

        // กิจกรรมวันนี้ (จาก movement summary) — แสดงตามประเภทที่กำหนด พร้อมจำนวนจริง
        const activityTypes = ["issue", "receipt", "return", "transfer", "adjustment"];
        const movementByType = {};
        (movement_summary || []).forEach((m) => {
            movementByType[m.movement_type] = Number(m.count || 0);
        });

        // สรุป PO ล่าสุด — มูลค่ารวม (คำนวณจากข้อมูลจริง)
        const poTotalAmount = (purchase_orders || []).reduce((sum, po) => sum + Number(po.total_amount || 0), 0);

        return (
            <div className="d-flex flex-row min-vh-100 bg-light w-100">
                <NavbarWarehouse page="dashboard" />

                <div className="body flex-grow-1 p-4 mt-5" style={{ backgroundColor: '#f8fafc', overflowY: 'auto' }}>

                    {/* 4 KPI Cards */}
                    <div className="row g-3 mb-4">
                        <div className="col-12 col-md-6 col-xl-3">
                            <div className="card border-0 rounded-4 shadow-sm h-100 p-3">
                                <div className="d-flex justify-content-between align-items-center">
                                    <div>
                                        <p className="text-secondary mb-1 small">{t('total_items')}</p>
                                        <h3 className="fw-bold text-dark mb-0">{this.formatNumber(summary.total_item)}</h3>
                                    </div>
                                    <div className="rounded-3 d-flex align-items-center justify-content-center" style={{ width: '48px', height: '48px', backgroundColor: colors.primary.bg, color: colors.primary.text }}>
                                        <span className="icon fs-5">{""}</span>
                                    </div>
                                </div>
                            </div>
                        </div>
                        <div className="col-12 col-md-6 col-xl-3">
                            <div className="card border-0 rounded-4 shadow-sm h-100 p-3">
                                <div className="d-flex justify-content-between align-items-center">
                                    <div>
                                        <p className="text-secondary mb-1 small">{t('in_stock')}</p>
                                        <h3 className="fw-bold text-success mb-0">{this.formatNumber(inStock)}</h3>
                                    </div>
                                    <div className="rounded-3 d-flex align-items-center justify-content-center" style={{ width: '48px', height: '48px', backgroundColor: colors.success.bg, color: colors.success.text }}>
                                        <span className="icon fs-5">{""}</span>
                                    </div>
                                </div>
                            </div>
                        </div>
                        <div className="col-12 col-md-6 col-xl-3">
                            <div className="card border-0 rounded-4 shadow-sm h-100 p-3">
                                <div className="d-flex justify-content-between align-items-center">
                                    <div>
                                        <p className="text-secondary mb-1 small">{t('low_stock_alert')}</p>
                                        <h3 className="fw-bold text-warning mb-0">{this.formatNumber(summary.low_stock)}</h3>
                                    </div>
                                    <div className="rounded-3 d-flex align-items-center justify-content-center" style={{ width: '48px', height: '48px', backgroundColor: colors.warning.bg, color: colors.warning.text }}>
                                        <span className="icon fs-5">{""}</span>
                                    </div>
                                </div>
                            </div>
                        </div>
                        <div className="col-12 col-md-6 col-xl-3">
                            <div className="card border-0 rounded-4 shadow-sm h-100 p-3">
                                <div className="d-flex justify-content-between align-items-center">
                                    <div>
                                        <p className="text-secondary mb-1 small">{t('out_of_stock')}</p>
                                        <h3 className="fw-bold text-danger mb-0">{this.formatNumber(summary.out_of_stock)}</h3>
                                    </div>
                                    <div className="rounded-3 d-flex align-items-center justify-content-center" style={{ width: '48px', height: '48px', backgroundColor: colors.danger.bg, color: colors.danger.text }}>
                                        <span className="icon fs-5">{""}</span>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Middle Section */}
                    <div className="row g-4 mb-4">
                        {/* Latest Purchase Orders */}
                        <div className="col-12 col-xl-8">
                            <div className="card border-0 rounded-4 shadow-sm h-100">
                                <div className="card-header bg-white border-bottom-0 pt-4 pb-2 px-4 d-flex justify-content-between align-items-center">
                                    <h6 className="mb-0 fw-bold"><span className="icon text-warning me-2">{""}</span> {t('latest_purchase_orders')}</h6>
                                    <span className="badge rounded-pill bg-primary-subtle text-primary px-3 py-1">{this.formatNumber(purchase_orders.length)} {t('items_unit')}</span>
                                </div>

                                {/* แถบสรุปมูลค่ารวม */}
                                {purchase_orders.length > 0 && (
                                    <div className="px-4 pb-3">
                                        <span className="text-secondary" style={{ fontSize: '12px' }}>
                                            {t('total_amount')} <span className="fw-bold text-dark">฿{this.formatNumber(poTotalAmount)}</span>
                                        </span>
                                    </div>
                                )}

                                <div className="card-body px-0 pt-0">
                                    <div className="list-group list-group-flush">
                                        {purchase_orders.length === 0 && (
                                            <div className="text-center text-secondary py-5" style={{ fontSize: '13px' }}>{t('no_data')}</div>
                                        )}
                                        {purchase_orders.map((po, index) => {
                                            const meta = this.poStatusMeta(po.status);
                                            const subLine = [po.supplier?.name, po.pr_number ? `${t('pr_number')}: ${po.pr_number}` : null, `${this.formatNumber(po.items?.length || 0)} ${t('items_unit')}`].filter(Boolean).join("  •  ") || "-";
                                            const amount = Number(po.total_amount || 0);
                                            return (
                                                <div key={index} className="list-group-item d-flex align-items-center py-3 px-4 border-bottom border-light">
                                                    <div className="rounded-3 d-flex align-items-center justify-content-center me-3 flex-shrink-0" style={{ width: '42px', height: '42px', backgroundColor: colors[meta.color].bg, color: colors[meta.color].text }}>
                                                        <span className="icon">{""}</span>
                                                    </div>
                                                    <div className="flex-grow-1 me-3" style={{ minWidth: 0 }}>
                                                        <div className="fw-bold text-dark" style={{ fontSize: '14px' }}>{po.po_number || "-"}</div>
                                                        <div className="text-secondary text-truncate" style={{ fontSize: '12px' }}>{subLine}</div>
                                                    </div>
                                                    <div className="d-flex flex-column align-items-end gap-1 flex-shrink-0">
                                                        <div className="d-flex align-items-center gap-2">
                                                            <span className={`badge rounded-pill fw-normal px-2 py-1 ${this.poStatusBadgeClass(po.status)}`} style={{ fontSize: '11px' }}>
                                                                {meta.label}
                                                            </span>
                                                            <span className="fw-bold text-dark" style={{ fontSize: '13px' }}>฿{this.formatNumber(amount)}</span>
                                                        </div>
                                                        <small className="text-secondary" style={{ fontSize: '11px' }}>{po.po_date ? format_date(po.po_date, "dd-mm-yyyy", "th") : "-"}</small>
                                                    </div>
                                                </div>
                                            );
                                        })}
                                    </div>
                                </div>
                            </div>
                        </div>

                        {/* Right column */}
                        <div className="col-12 col-xl-4 d-flex flex-column gap-4">
                            {/* Stock status chart */}
                            <div className="card border-0 rounded-4 shadow-sm">
                                <div className="card-body p-4">
                                    <h6 className="mb-3 fw-bold"><span className="icon text-primary me-2">{""}</span> {t('stock_status')}</h6>
                                    {hasStock ? (
                                        <div style={{ height: '200px' }}>
                                            <Pie data={stockStatusData} options={stockStatusOptions} />
                                        </div>
                                    ) : (
                                        <div className="text-center text-secondary py-5" style={{ fontSize: '13px' }}>{t('no_data')}</div>
                                    )}
                                </div>
                            </div>

                            {/* Today activity */}
                            <div className="card border-0 rounded-4 shadow-sm">
                                <div className="card-body p-4">
                                    <h6 className="mb-3 fw-bold"><span className="icon text-primary me-2">{""}</span> {t('today_activity')}</h6>
                                    {activityTypes.map((type, index) => {
                                        const meta = this.movementMeta(type);
                                        return (
                                            <div key={type} className={`d-flex justify-content-between align-items-center ${index < activityTypes.length - 1 ? 'mb-2' : ''}`}>
                                                <span className="text-secondary d-flex align-items-center gap-2" style={{ fontSize: '14px' }}>
                                                    <span className="icon" style={{ color: colors[meta.color].text }}>{meta.icon}</span>
                                                    {meta.label}
                                                </span>
                                                <span className="fw-bold text-dark" style={{ fontSize: '14px' }}>{this.formatNumber(movementByType[type] || 0)} {t('items_unit')}</span>
                                            </div>
                                        );
                                    })}
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Low Stock Table */}
                    <div className="card border-0 rounded-4 shadow-sm">
                        <div className="card-header bg-white border-bottom-0 pt-4 pb-3 px-4 d-flex justify-content-between align-items-center">
                            <h6 className="mb-0 fw-bold d-flex align-items-center">
                                <span className="icon text-warning me-2">{""}</span> {t('low_stock_table_title')}
                            </h6>
                            <span className="badge rounded-pill bg-warning text-dark px-3 py-1">{this.formatNumber(summary.low_stock)}</span>
                        </div>
                        <div className="card-body px-0 pt-0 pb-2">
                            <div className="table-responsive">
                                <table className="table align-middle mb-0" style={{ fontSize: '13px' }}>
                                    <thead className="text-secondary bg-light" style={{ borderTop: '1px solid #f1f5f9', borderBottom: '1px solid #f1f5f9' }}>
                                        <tr>
                                            <th className="ps-4 py-3 border-0 fw-bold">{t('part_code')}</th>
                                            <th className="py-3 border-0 fw-bold">{t('description')}</th>
                                            <th className="text-center py-3 border-0 fw-bold">{t('on_hand')}</th>
                                            <th className="text-center py-3 border-0 fw-bold">{t('reserved')}</th>
                                            <th className="text-center py-3 border-0 fw-bold">{t('available')}</th>
                                            <th className="text-center py-3 border-0 fw-bold">{t('min_stock_label')}</th>
                                            <th className="text-center pe-4 py-3 border-0 fw-bold">{t('status')}</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {low_stock_items.length === 0 && (
                                            <tr>
                                                <td colSpan="7" className="text-center text-secondary py-5">{t('no_data')}</td>
                                            </tr>
                                        )}
                                        {low_stock_items.map((item, index) => {
                                            const available = Number(item.available_qty || 0);
                                            const isOut = available <= 0;
                                            // เกณฑ์เดียวกับที่ backend ใช้นับสต็อกต่ำ: ใช้ min_stock ก่อน ถ้าไม่ตั้งค่อยใช้ reorder_point
                                            const minStock = Number(item.part?.min_stock || 0);
                                            const threshold = minStock > 0 ? minStock : Number(item.part?.reorder_point || 0);
                                            return (
                                                <tr key={index} style={{ borderBottom: '1px solid #f8fafc' }}>
                                                    <td className="ps-4 py-3 text-primary fw-medium">{item.part?.part_number || "-"}</td>
                                                    <td className="py-3 text-dark">{item.part?.name || item.part?.description || "-"}</td>
                                                    <td className="text-center py-3 text-dark">{this.formatNumber(item.balance_qty)}</td>
                                                    <td className="text-center py-3 text-dark">{this.formatNumber(item.reserved_qty)}</td>
                                                    <td className={`text-center py-3 fw-bold ${isOut ? 'text-danger' : 'text-warning'}`}>{this.formatNumber(available)}</td>
                                                    <td className="text-center py-3 text-dark">{this.formatNumber(threshold)}</td>
                                                    <td className="text-center pe-4 py-3">
                                                        <span className={`badge rounded-pill px-3 py-1 fw-normal ${isOut ? 'bg-danger-light text-danger' : 'bg-warning-light text-warning'}`}>
                                                            {isOut ? t('status_out_of_stock') : t('status_low')}
                                                        </span>
                                                    </td>
                                                </tr>
                                            );
                                        })}
                                    </tbody>
                                </table>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        );
    }
}
